import { httpClient } from '@/core/api/httpClient';
import type {
  BoardStatus,
  CreatedResponse,
  CreateBoardStatusPayload,
  UpdateBoardStatusPayload,
  ReorderPayload,
  StatusType,
} from '../types';
import { mockBoardStatuses } from './board-statuses.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const STATUS_TYPE_TO_INT: Record<StatusType, number> = {
  Todo: 0,
  InProgress: 1,
  Done: 2,
};

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const boardStatusesMock: BoardStatus[] = [...mockBoardStatuses];

export const boardStatusesService = {
  getByBoard: async (boardId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({
        data: boardStatusesMock
          .filter((s) => s.boardId === boardId)
          .sort((a, b) => a.order - b.order),
      });
    }
    return httpClient.get<BoardStatus[]>(`${API_V1}/boards/${boardId}/statuses`, { signal });
  },

  create: async (boardId: string, data: CreateBoardStatusPayload) => {
    if (USE_MOCK) {
      const existing = boardStatusesMock.filter((s) => s.boardId === boardId);
      const newStatus: BoardStatus = {
        id: `bs-${Date.now()}`,
        boardId,
        name: data.name,
        color: data.color,
        type: data.type,
        order: existing.length,
        wipLimit: data.wipLimit ?? null,
      };
      boardStatusesMock.push(newStatus);
      return Promise.resolve({ data: { id: newStatus.id } as CreatedResponse });
    }
    return httpClient.post<Record<string, unknown>, CreatedResponse>(
      `${API_V1}/boards/${boardId}/statuses`,
      { ...data, type: STATUS_TYPE_TO_INT[data.type] },
    );
  },

  update: async (boardId: string, statusId: string, data: UpdateBoardStatusPayload) => {
    if (USE_MOCK) {
      const idx = boardStatusesMock.findIndex((s) => s.id === statusId && s.boardId === boardId);
      if (idx !== -1) boardStatusesMock[idx] = { ...boardStatusesMock[idx], ...data };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<Record<string, unknown>, void>(
      `${API_V1}/boards/${boardId}/statuses/${statusId}`,
      { ...data, ...(data.type !== undefined && { type: STATUS_TYPE_TO_INT[data.type] }) },
    );
  },

  delete: async (boardId: string, statusId: string) => {
    if (USE_MOCK) {
      const idx = boardStatusesMock.findIndex((s) => s.id === statusId && s.boardId === boardId);
      if (idx !== -1) boardStatusesMock.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/boards/${boardId}/statuses/${statusId}`);
  },

  reorder: async (boardId: string, data: ReorderPayload) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<ReorderPayload, void>(
      `${API_V1}/boards/${boardId}/statuses/reorder`,
      data,
    );
  },
};
